import { createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { EmptyState, ErrorAlert, LoadingRows } from '../components/states';
import { flatten, useCreateSshKey, useSshKeys } from '../lib/queries';
import { pushToast } from '../lib/toast';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Dialog } from '../ui/Dialog';
import { Input, Textarea } from '../ui/Field';
import { Icon } from '../ui/Icon';

const KEY_PATTERN = /^(ssh-rsa|ssh-ed25519|ecdsa-sha2-nistp(256|384|521)|sk-ssh-ed25519@openssh\.com|sk-ecdsa-sha2-nistp256@openssh\.com)\s+[A-Za-z0-9+/=]+(\s+\S+)?$/;

export function SshKeys(): JSX.Element {
  const keys = useSshKeys();
  const create = useCreateSshKey();
  const items = createMemo(() => flatten(keys.data?.pages));

  const [open, setOpen] = createSignal(false);
  const [display, setDisplay] = createSignal('');
  const [publicKey, setPublicKey] = createSignal('');
  const [touched, setTouched] = createSignal(false);

  const keyError = createMemo(() => {
    if (!touched()) return undefined;
    const value = publicKey().trim();
    if (!value) return 'Paste the contents of your public key file.';
    if (!KEY_PATTERN.test(value)) return 'This does not look like an OpenSSH public key (e.g. ssh-ed25519 AAAA…).';
    return undefined;
  });

  function reset(): void {
    setDisplay('');
    setPublicKey('');
    setTouched(false);
    setOpen(false);
  }

  async function submit(): Promise<void> {
    setTouched(true);
    if (keyError() || !publicKey().trim()) return;
    try {
      await create.mutateAsync({ display: display().trim() || undefined, public_key: publicKey().trim() });
      pushToast('success', 'SSH key registered.');
      reset();
    } catch {
      // Shown in the dialog.
    }
  }

  return (
    <main class="page page--narrow">
      <div class="page__head">
        <div class="page__title">
          <h1>SSH keys</h1>
          <span class="page__subtitle">Public keys you can install on a box when creating it.</span>
        </div>
        <div class="page__actions">
          <Button onClick={() => setOpen(true)}>
            <Icon name="plus" />
            Add key
          </Button>
        </div>
      </div>

      <Show when={keys.isError}>
        <ErrorAlert title="Could not load SSH keys" error={keys.error} onRetry={() => void keys.refetch()} />
      </Show>

      <Show when={!keys.isPending} fallback={<LoadingRows rows={2} height={80} />}>
        <Show
          when={items().length > 0}
          fallback={
            <EmptyState
              icon="key"
              title="No SSH keys yet"
              text="Add the public half of a key pair — typically ~/.ssh/id_ed25519.pub — to be able to log in to your boxes."
              action={<Button onClick={() => setOpen(true)}>Add your first key</Button>}
            />
          }
        >
          <div class="list">
            <For each={items()}>
              {(key) => (
                <Card padding="md">
                  <div class="row gap-4">
                    <Icon name="key" size={18} />
                    <div class="list-row__main">
                      <span class="list-row__title">{key.display}</span>
                      <span class="list-row__meta mono">{key.public_key}</span>
                    </div>
                  </div>
                  <div class="kv" style={{ 'margin-top': 'var(--space-3)' }}>
                    <span class="kv__k">Fingerprint</span>
                    <span class="kv__v kv__v--mono">{key.hash}</span>
                  </div>
                </Card>
              )}
            </For>
          </div>
        </Show>
      </Show>

      <Show when={keys.hasNextPage}>
        <div class="row gap-3" style={{ 'justify-content': 'center', 'margin-top': 'var(--space-6)' }}>
          <Button variant="secondary" loading={keys.isFetchingNextPage} onClick={() => void keys.fetchNextPage()}>
            Load more
          </Button>
        </div>
      </Show>

      <Dialog
        open={open()}
        onClose={reset}
        title="Add an SSH public key"
        description="Paste the public key itself, never the private one."
        width={560}
        footer={
          <>
            <Button variant="secondary" onClick={reset} disabled={create.isPending}>
              Cancel
            </Button>
            <Button loading={create.isPending} onClick={submit}>
              Add key
            </Button>
          </>
        }
      >
        <div class="stack gap-4">
          <Input
            label="Name (optional)"
            placeholder="Work laptop"
            value={display()}
            onInput={(event) => setDisplay(event.currentTarget.value)}
          />
          <Textarea
            label="Public key"
            rows={5}
            placeholder="ssh-ed25519 AAAAC3NzaC1lZDI1NTE5… user@host"
            value={publicKey()}
            onInput={(event) => setPublicKey(event.currentTarget.value)}
            onBlur={() => setTouched(true)}
            error={keyError()}
          />
          <Show when={create.isError}>
            <ErrorAlert title="Could not register the key" error={create.error} />
          </Show>
        </div>
      </Dialog>
    </main>
  );
}
